import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast, Toaster } from "sonner";
import { listWorkers, createWorker, deleteWorker } from "@/lib/workers";
import { UserCircle, Plus, Trash, ArrowRight, X, ChartLine } from "@phosphor-icons/react";

export default function Workers() {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", employee_id: "", job_title: "", department: "", notes: "" });
  const [creating, setCreating] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try { setWorkers(await listWorkers()); }
    catch (e) { toast.error("Could not load workers"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setCreating(true);
    try {
      const w = await createWorker(form);
      toast.success(`Added ${w.name}`);
      setShowAdd(false);
      setForm({ name: "", employee_id: "", job_title: "", department: "", notes: "" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add worker"); }
    finally { setCreating(false); }
  };

  const onDelete = async (id, name) => {
    if (!window.confirm(`Delete ${name}? Their past analyses will remain but be unlinked.`)) return;
    try { await deleteWorker(id); toast.success("Worker removed"); await load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar variant="app" />
      <Toaster richColors position="top-right" />
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500 mb-2">Workers</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-ink-900">Roster</h1>
            <p className="mt-2 text-ink-700 max-w-2xl">Attach each analysis to a worker. SafeForm.ai tracks their risk score over time so you can prove your safety program is actually working.</p>
          </div>
          <Button data-testid="workers-add-cta" size="lg" onClick={() => setShowAdd(true)} className="rounded-none bg-safety hover:bg-safety-hover text-white h-12 px-6">
            <Plus size={16} weight="bold" className="mr-2" /> Add worker
          </Button>
        </div>

        <div className="mt-10">
          {loading ? (
            <div className="border border-ink-100 p-16 text-center text-ink-500 font-mono text-xs uppercase tracking-[0.3em]">Loading…</div>
          ) : workers.length === 0 ? (
            <div className="border border-ink-100 p-16 text-center bg-ink-50">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-ink-900 mb-4"><UserCircle size={20} weight="bold" className="text-white" /></div>
              <div className="font-display font-bold text-2xl tracking-tight text-ink-900">No workers yet</div>
              <p className="mt-2 text-ink-500 max-w-md mx-auto">Add the people whose tasks you're analyzing. You can attach a worker when you upload a new clip.</p>
              <Button data-testid="workers-empty-add" onClick={() => setShowAdd(true)} className="mt-6 rounded-none bg-safety hover:bg-safety-hover text-white" size="lg">
                <Plus size={16} weight="bold" className="mr-2" /> Add your first worker
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {workers.map((w) => (
                <div key={w.id} className="border border-ink-100 p-6 sf-card bg-white" data-testid={`worker-card-${w.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="w-10 h-10 bg-ink-900 text-white flex items-center justify-center font-display font-bold">{w.name?.[0]?.toUpperCase() || "?"}</div>
                    <button onClick={() => onDelete(w.id, w.name)} className="text-ink-500 hover:text-hazard-critical" aria-label="Delete" data-testid={`worker-del-${w.id}`}>
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                  <div className="mt-4 font-display font-bold text-xl tracking-tight text-ink-900">{w.name}</div>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-ink-500 truncate">
                    {[w.job_title, w.department].filter(Boolean).join(" · ") || "—"}
                  </div>
                  {w.employee_id && (
                    <div className="mt-1 font-mono text-[10px] text-ink-500">EMP # {w.employee_id}</div>
                  )}
                  <div className="mt-5 flex items-center justify-between">
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-ink-50 border border-ink-100 font-mono text-[10px] uppercase tracking-[0.25em] text-ink-700">
                      <ChartLine size={11} weight="bold" /> {w.analysis_count || 0} analyses
                    </span>
                    <Link to={`/dashboard/workers/${w.id}`}>
                      <Button variant="outline" size="sm" data-testid={`worker-open-${w.id}`} className="rounded-none border-ink-100 hover:border-ink-900">
                        Open <ArrowRight size={12} weight="bold" className="ml-1" />
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 bg-ink-900/60 flex items-center justify-center p-4" onClick={() => !creating && setShowAdd(false)}>
          <div className="bg-white border-2 border-ink-900 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="worker-add-modal">
            <div className="flex items-center justify-between p-5 border-b border-ink-100">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Add worker</div>
              <button onClick={() => setShowAdd(false)} className="text-ink-500 hover:text-ink-900"><X size={16} weight="bold" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Name *</Label>
                <Input data-testid="worker-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Marcus Cole" className="mt-2 rounded-none border-ink-100 focus-visible:ring-safety" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Employee #</Label>
                  <Input data-testid="worker-form-emp" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="mt-2 rounded-none border-ink-100" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Job title</Label>
                  <Input data-testid="worker-form-title" value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} placeholder="Picker / Line tech" className="mt-2 rounded-none border-ink-100" />
                </div>
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Department</Label>
                <Input data-testid="worker-form-dept" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="Bay 3 / Floor 2" className="mt-2 rounded-none border-ink-100" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Notes</Label>
                <Textarea data-testid="worker-form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-2 rounded-none border-ink-100" />
              </div>
            </div>
            <div className="p-5 border-t border-ink-100 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAdd(false)} className="rounded-none border-ink-100" disabled={creating}>Cancel</Button>
              <Button data-testid="worker-form-submit" onClick={submit} disabled={creating} className="rounded-none bg-safety hover:bg-safety-hover text-white">
                {creating ? "Adding…" : "Add worker"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
